import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Image, Badge } from "uikit-react";
import UIkit from "uikit";
import { Link as RawLink } from "../ui-kit/Link";
import { AppContexts } from "../../providers";
import FEATURES from "../../config/features";

import Notification from "./notification";
import TrialAccount from "../common/TrialAccount";

const Link = ({ onClick, ...props }) => (
  <RawLink
    onClick={() => {
      onClick && onClick();
    }}
    {...props}
  />
);

const SidebarItem = ({
  to = "#",
  label,
  icon,
  avatarSrc,
  className = "",
  collapsed,
  onClick,
  badge,
  children,
  isOpen,
  onToggle,
  isFlyoutOpen,
  onFlyoutToggle,
}) => {
  const hasChildren = !!children;

  const leftContent = (
    <span className="sidebar-link-left">
      {avatarSrc ? (
        <span className="sidebar-icon-wrap">
          <img src={avatarSrc} className="sidebar-avatar" alt={label} />
        </span>
      ) : icon ? (
        <span className="sidebar-icon-wrap">
          <span uk-icon={icon} className="sidebar-icon"></span>
        </span>
      ) : null}

      {!collapsed && <span className="sidebar-label">{label}</span>}
    </span>
  );

  if (hasChildren) {
    if (collapsed) {
      return (
        <li className={`sidebar-item has-children ${className} ${isFlyoutOpen ? "flyout-open" : ""}`}>
          <button
            type="button"
            className="sidebar-link sidebar-link-button"
            onClick={() => onFlyoutToggle && onFlyoutToggle()}
            uk-tooltip={label}
          >
            {leftContent}
          </button>

          {isFlyoutOpen && (
            <div className="sidebar-flyout">
              <div className="sidebar-flyout-title">{label}</div>
              <ul className="sidebar-flyout-list">
                {children.items}
              </ul>
            </div>
          )}
        </li>
      );
    }

    return (
      <li className={`sidebar-item has-children ${className}`}>
        <button
          type="button"
          className="sidebar-link sidebar-link-button"
          onClick={() => onToggle && onToggle()}
        >
          {leftContent}

          <span className={`sidebar-chevron ${isOpen ? "open" : ""}`}>
            <span uk-icon="chevron-down"></span>
          </span>
        </button>

        {isOpen && <ul className="sidebar-subnav">{children.items}</ul>}
      </li>
    );
  }

  return (
    <li className={`sidebar-item ${className}`}>
      <Link
        to={to}
        className="sidebar-link"
        onClick={onClick}
        uk-tooltip={collapsed ? label : ""}
      >
        {leftContent}

        {!collapsed && badge ? (
          <Badge className="hms-notification-count" count={badge} />
        ) : null}
      </Link>
    </li>
  );
};

const SidebarSubItem = ({ to, label, onClick, flyout = false }) => (
  <li className={flyout ? "sidebar-flyout-item" : "sidebar-subitem"}>
    <Link
      to={to}
      onClick={onClick}
      className={flyout ? "sidebar-flyout-link" : "sidebar-subnav-link"}
    >
      <span className={flyout ? "sidebar-flyout-label" : "sidebar-subnav-label"}>
        {label}
      </span>
    </Link>
  </li>
);

const Sidebar = () => {
  const { user: account, inbox, logout } = useContext(AppContexts.AuthContext);
  const { newOrderCount, getNewOrderCount } = useContext(
    AppContexts.OrdersContext,
  );

  const location = useLocation();
  const [collapsed, setCollapsed] = useState(true);
  const [openMenu, setOpenMenu] = useState(null);
  const [flyoutMenu, setFlyoutMenu] = useState(null);
  const [mobileOpenMenu, setMobileOpenMenu] = useState(null);

    useEffect(() => {
        getNewOrderCount();
    }, []);

    useEffect(() => {
        setCollapsed(true);
        setOpenMenu(null);
        setFlyoutMenu(null);
        setMobileOpenMenu(null);
        const mobileMenu = document.getElementById('mobile-menu');
        if (mobileMenu) {
            UIkit.offcanvas(mobileMenu).hide();
        }
    }, [location.pathname]);

  const handleLogout = async () => {
    logout()
      .then(() => {
        console.log("Goodbye");
      })
      .catch((err) => {
        console.warn("Error logging out:", err);
      });
  };

  const profileImage = useMemo(() => {
    return (
      (account?.user?.attachments || []).find((a) => a.type === "image") || null
    );
  }, [account]);

  const isActive = (paths) => {
    if (!Array.isArray(paths)) paths = [paths];

    return paths.some((path) => {
      const regex = new RegExp(`^${path}`);
      return regex.test(location.pathname);
    })
      ? "is-active"
      : "";
  };

  const hasExtras = () => {
    return (account?.details?.product || "").split(",").includes("extras");
  };

  if (!account) return null;

  return (
    <>
      <div className={`rbr-sidebar ${collapsed ? "collapsed" : ""}`}>
        <div className="rbr-sidebar-top">
          <div className="rbr-sidebar-brand">
            <Link to="/" className="rbr-sidebar-logo">
              <Image src="/images/ruebaur-logo.png" alt="RueBaRue" />
            </Link>

            <button
            type="button"
            className="rbr-sidebar-toggle"
            onClick={() => setCollapsed((prev) => !prev)}
            title={collapsed ? "Expand" : "Collapse"}
            >
            <span uk-icon={collapsed ? "chevron-right" : "chevron-left"}></span>
            </button>
          </div>

          {!!account && account?.details?.type !== "onboarding" && (
            <ul className="sidebar-nav">
                <SidebarItem
                to="/messages"
                label="Messages"
                icon="comment"
                className={isActive("/messages")}
                collapsed={collapsed}
                />
                <SidebarItem
                to="/guests"
                label="Guests"
                icon="users"
                className={isActive("/guests")}
                collapsed={collapsed}
                />
                <SidebarItem
                to="/contacts"
                label="Contacts"
                icon="user"
                className={isActive("/contacts")}
                collapsed={collapsed}
                />
                {hasExtras() && (
                <SidebarItem
                    to="/orders"
                    label="Orders"
                    icon="cart"
                    className={isActive("/orders")}
                    collapsed={collapsed}
                    badge={+newOrderCount > 0 ? +newOrderCount : null}
                />
                )}

                <li className="sidebar-divider"></li>

                <SidebarItem
                    label="Messaging"
                    icon="comments"
                    className={isActive([
                        "/scheduler",
                        "/extend-guest-stay",
                        "/alerts",
                        "/surveys",
                        "/saved-responses",
                        "/message-templates",
                    ])}
                    collapsed={collapsed}
                    isOpen={openMenu === "messaging"}
                    onToggle={() =>
                        setOpenMenu((prev) => (prev === "messaging" ? null : "messaging"))
                    }
                    isFlyoutOpen={flyoutMenu === "messaging"}
                    onFlyoutToggle={() => {
                        if (collapsed) {
                            setCollapsed(false);
                        }
                        setOpenMenu((prev) => (prev === "messaging" ? null : "messaging"));
                        setFlyoutMenu(null);
                    }}
                    children={{
                        items: (
                        <>
                            <SidebarSubItem to="/scheduler" label="Scheduler" flyout={collapsed} />
                            <SidebarSubItem to="/extend-guest-stay" label="Extend Stays" flyout={collapsed} />
                            <SidebarSubItem to="/alerts" label="Alerts" flyout={collapsed} />
                            <SidebarSubItem to="/surveys" label="Surveys" flyout={collapsed} />
                            <SidebarSubItem to="/saved-responses" label="Saved Responses" flyout={collapsed} />
                            <SidebarSubItem to="/message-templates" label="Message Templates" flyout={collapsed} />
                        </>
                        ),
                    }}
                    />

                <SidebarItem
                    label="Operations"
                    icon="settings"
                    className={isActive([
                        "/operations",
                        "/operations/calender",
                        "/operations/housekeeping",
                        "/operations/inspections",
                        "/operations/maintenance",
                        "/operations/rules",
                        "/operations/storage/locations",
                        "/operations/storage/items",
                        "/operations/storage/stock",
                        "/operations/storage/movements",
                        "/operations/home-inventory",
                        "/operations/standards-structure",
                        "/operations/property-setup",
                        "/operations/checklists",
                        "/work-orders",
                        "/work-schedules",
                        "/availability-calendar",
                    ])}
                    collapsed={collapsed}
                    isOpen={openMenu === "operations"}
                    onToggle={() =>
                        setOpenMenu((prev) => (prev === "operations" ? null : "operations"))
                    }
                    isFlyoutOpen={flyoutMenu === "operations"}
                    onFlyoutToggle={() => {
                        if (collapsed) {
                            setCollapsed(false);
                        }
                        setOpenMenu((prev) => (prev === "operations" ? null : "operations"));
                        setFlyoutMenu(null);
                    }}
                    children={{
                        items: (
                        <>
                            <SidebarSubItem to="/operations" label="Dashboard" flyout={collapsed} />
                            {FEATURES.CALENDAR_ENABLED && (
                            <SidebarSubItem
                                to="/operations/calender"
                                label="Calendar"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.HOUSEKEEPING_ENABLED && (
                            <SidebarSubItem
                                to="/operations/housekeeping"
                                label="Housekeeping"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.INSPECTIONS_ENABLED && (
                            <SidebarSubItem
                                to="/operations/inspections"
                                label="Inspections"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.MAINTENANCE_ENABLED && (
                            <SidebarSubItem
                                to="/operations/maintenance"
                                label="Maintenance"
                                flyout={collapsed}
                            />
                            )}
                            <SidebarSubItem
                            to="/work-orders"
                            label="Work Orders"
                            flyout={collapsed}
                            />
                            {FEATURES.RULES_ENABLED && (
                            <SidebarSubItem
                                to="/operations/rules"
                                label="Rules"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.STORAGE_LOCATIONS_ENABLED && (
                            <SidebarSubItem
                                to="/operations/storage/locations"
                                label="Storage Locations"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.STORAGE_ITEMS_ENABLED && (
                            <SidebarSubItem
                                to="/operations/storage/items"
                                label="Storage Items"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.MANAGE_STOCK_ENABLED && (
                            <SidebarSubItem
                                to="/operations/storage/stock"
                                label="Manage Stock"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.STOCK_MOVEMENTS_ENABLED && (
                            <SidebarSubItem
                                to="/operations/storage/movements"
                                label="Stock Movements"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.HOME_INVENTORY_ENABLED && (
                            <SidebarSubItem
                                to="/operations/home-inventory"
                                label="Home Inventory"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.CLEANING_STANDARDS_ENABLED && (
                            <SidebarSubItem
                                to="/operations/standards-structure"
                                label="Cleaning & Inspection Standards"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.PROPERTY_SETUP_ENABLED && (
                            <SidebarSubItem
                                to="/operations/property-setup"
                                label="Property Setup"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.CHECKLISTS_ENABLED && (
                            <SidebarSubItem
                                to="/operations/checklists"
                                label="Checklists"
                                flyout={collapsed}
                            />
                            )}
                            <SidebarSubItem
                            to="/work-schedules"
                            label="Work Schedules"
                            flyout={collapsed}
                            />
                            <SidebarSubItem
                            to="/availability-calendar"
                            label="Availability Calendar"
                            flyout={collapsed}
                            />
                        </>
                        ),
                    }}
                    />

                <SidebarItem
                    label="Guest Guides"
                    icon="album"
                    className={isActive([
                        "/master-home-guide",
                        "/properties",
                        "/extras",
                        "/areas",
                        "/property-field-values",
                        "/subscriptions",
                    ])}
                    collapsed={collapsed}
                    isOpen={openMenu === "guides"}
                    onToggle={() =>
                        setOpenMenu((prev) => (prev === "guides" ? null : "guides"))
                    }
                    isFlyoutOpen={flyoutMenu === "guides"}
                    onFlyoutToggle={() => {
                        if (collapsed) {
                            setCollapsed(false);
                        }
                        setOpenMenu((prev) => (prev === "guides" ? null : "guides"));
                        setFlyoutMenu(null);
                    }}
                    children={{
                        items: (
                        <>
                            <SidebarSubItem
                            to="/master-home-guide"
                            label="Master Home Guide"
                            flyout={collapsed}
                            />
                            <SidebarSubItem
                            to="/properties"
                            label="Home Guides"
                            flyout={collapsed}
                            />
                            {hasExtras() && (
                            <SidebarSubItem
                                to="/extras"
                                label="Extras Guide"
                                flyout={collapsed}
                            />
                            )}
                            <SidebarSubItem
                            to="/areas"
                            label="Area Guides"
                            flyout={collapsed}
                            />
                            <SidebarSubItem
                            to="/property-field-values"
                            label="Property Field Values"
                            flyout={collapsed}
                            />
                            <SidebarSubItem
                            to="/subscriptions"
                            label="Subscriptions"
                            flyout={collapsed}
                            />
                        </>
                        ),
                    }}
                    />

                <li className="sidebar-divider"></li>

                <SidebarItem
                to="/tasks"
                label="My Tasks"
                icon="check"
                className={isActive("/tasks")}
                collapsed={collapsed}
                />

                <SidebarItem
                    to="/reports"
                    label="Reports"
                    icon="thumbnails"
                    className={isActive("/reports")}
                    collapsed={collapsed}
                    />

            </ul>
            )}
        </div>

       {!!account && (
        <div className="rbr-sidebar-bottom">
          <div className="sidebar-account-info">
            <div className="sidebar-account-info-row">
              <span className="sidebar-account-info-label">Signed in as</span>
              <span className="sidebar-account-info-value" title={account?.user?.email}>{account?.user?.email}</span>
            </div>
            {inbox && inbox.phone && (
              <div className="sidebar-account-info-row">
                <span className="sidebar-account-info-label">SMS Number</span>
                <span className="sidebar-account-info-value">{inbox.phone}</span>
              </div>
            )}
          </div>
        </div>
        )}

      </div>

      <button
        id="sidebar-mobile-toggle"
        className="uk-hidden@xl rbr-mobile-toggle"
        uk-toggle="target: #mobile-menu; animation: uk-animation-fade"
        aria-expanded="false"
      >
        <span uk-navbar-toggle-icon=""></span>
      </button>

      <div id="mobile-menu" uk-offcanvas="overlay: true">
        <div className="uk-offcanvas-bar mobile-sidebar">
          <div className="mobile-sidebar-header">
            <Link to="/" className="rbr-sidebar-logo">
              <Image src="/images/ruebaur-logo.png" alt="RueBaRue" />
            </Link>
            <button
              className="uk-offcanvas-close mobile-sidebar-close"
              type="button"
              uk-close
            >
              <Image src="/images/close-small.svg" alt="close" />
            </button>
          </div>

          {!!account && (
            <div className="mobile-menu-profile">
              <Link to="/profile" className="mobile-menu-profile-link">
                <img
                  src={profileImage?.url || "/images/profile.svg"}
                  className="mobile-menu-avatar"
                  alt="Profile"
                />
                <div className="mobile-menu-profile-info">
                  <span className="mobile-menu-profile-name">
                    {account?.user?.first_name || account?.user?.email}
                  </span>
                  <span className="mobile-menu-profile-email">
                    {account?.user?.email}
                  </span>
                </div>
              </Link>
            </div>
          )}

          {!!account && account?.details?.type !== "onboarding" && (
          <ul className="sidebar-nav">
            <SidebarItem to="/messages" label="Messages" icon="comment" className={isActive("/messages")} collapsed={false} />
            <SidebarItem to="/guests" label="Guests" icon="users" className={isActive("/guests")} collapsed={false} />
            <SidebarItem to="/contacts" label="Contacts" icon="user" className={isActive("/contacts")} collapsed={false} />
            {hasExtras() && (
              <SidebarItem to="/orders" label="Orders" icon="cart" className={isActive("/orders")} collapsed={false} badge={+newOrderCount > 0 ? +newOrderCount : null} />
            )}

            <li className="sidebar-divider"></li>

            <SidebarItem
              label="Messaging"
              icon="mail"
              className={isActive(["/scheduler", "/extend-guest-stay", "/alerts", "/surveys", "/saved-responses", "/message-templates"])}
              collapsed={false}
              isOpen={mobileOpenMenu === "messaging"}
              onToggle={() => setMobileOpenMenu((prev) => (prev === "messaging" ? null : "messaging"))}
              children={{
                items: (
                  <>
                    <SidebarSubItem to="/scheduler" label="Scheduler" />
                    <SidebarSubItem to="/extend-guest-stay" label="Extend Stays" />
                    <SidebarSubItem to="/alerts" label="Alerts" />
                    <SidebarSubItem to="/surveys" label="Surveys" />
                    <SidebarSubItem to="/saved-responses" label="Saved Responses" />
                    <SidebarSubItem to="/message-templates" label="Message Templates" />
                  </>
                ),
              }}
            />

            <SidebarItem
              label="Operations"
              icon="settings"
              className={isActive(["/operations", "/work-orders", "/work-schedules", "/availability-calendar"])}
              collapsed={false}
              isOpen={mobileOpenMenu === "operations"}
              onToggle={() => setMobileOpenMenu((prev) => (prev === "operations" ? null : "operations"))}
              children={{
                items: (
                  <>
                    <SidebarSubItem to="/operations" label="Dashboard" />
                    {FEATURES.CALENDAR_ENABLED && <SidebarSubItem to="/operations/calender" label="Calendar" />}
                    {FEATURES.HOUSEKEEPING_ENABLED && <SidebarSubItem to="/operations/housekeeping" label="Housekeeping" />}
                    {FEATURES.INSPECTIONS_ENABLED && <SidebarSubItem to="/operations/inspections" label="Inspections" />}
                    {FEATURES.MAINTENANCE_ENABLED && <SidebarSubItem to="/operations/maintenance" label="Maintenance" />}
                    <SidebarSubItem to="/work-orders" label="Work Orders" />
                    {FEATURES.RULES_ENABLED && <SidebarSubItem to="/operations/rules" label="Rules" />}
                    {FEATURES.STORAGE_LOCATIONS_ENABLED && <SidebarSubItem to="/operations/storage/locations" label="Storage Locations" />}
                    {FEATURES.STORAGE_ITEMS_ENABLED && <SidebarSubItem to="/operations/storage/items" label="Storage Items" />}
                    {FEATURES.MANAGE_STOCK_ENABLED && <SidebarSubItem to="/operations/storage/stock" label="Manage Stock" />}
                    {FEATURES.STOCK_MOVEMENTS_ENABLED && <SidebarSubItem to="/operations/storage/movements" label="Stock Movements" />}
                    {FEATURES.HOME_INVENTORY_ENABLED && <SidebarSubItem to="/operations/home-inventory" label="Home Inventory" />}
                    {FEATURES.CLEANING_STANDARDS_ENABLED && <SidebarSubItem to="/operations/standards-structure" label="Cleaning & Inspection Standards" />}
                    {FEATURES.PROPERTY_SETUP_ENABLED && <SidebarSubItem to="/operations/property-setup" label="Property Setup" />}
                    {FEATURES.CHECKLISTS_ENABLED && <SidebarSubItem to="/operations/checklists" label="Checklists" />}
                    <SidebarSubItem to="/work-schedules" label="Work Schedules" />
                    <SidebarSubItem to="/availability-calendar" label="Availability Calendar" />
                  </>
                ),
              }}
            />

            <SidebarItem
              label="Guest Guides"
              icon="album"
              className={isActive(["/master-home-guide", "/properties", "/extras", "/areas", "/property-field-values", "/subscriptions"])}
              collapsed={false}
              isOpen={mobileOpenMenu === "guides"}
              onToggle={() => setMobileOpenMenu((prev) => (prev === "guides" ? null : "guides"))}
              children={{
                items: (
                  <>
                    <SidebarSubItem to="/master-home-guide" label="Master Home Guide" />
                    <SidebarSubItem to="/properties" label="Home Guides" />
                    {hasExtras() && <SidebarSubItem to="/extras" label="Extras Guide" />}
                    <SidebarSubItem to="/areas" label="Area Guides" />
                    <SidebarSubItem to="/property-field-values" label="Property Field Values" />
                    <SidebarSubItem to="/subscriptions" label="Subscriptions" />
                  </>
                ),
              }}
            />

            <li className="sidebar-divider"></li>

            <SidebarItem to="/tasks" label="My Tasks" icon="check" className={isActive("/tasks")} collapsed={false} />
            <SidebarItem to="/reports" label="Reports" icon="table" className={isActive("/reports")} collapsed={false} />

            <li className="sidebar-divider"></li>

            <SidebarItem to="/profile" label="Profile" icon="user" className={isActive("/profile")} collapsed={false} />
            <SidebarItem
              to="#"
              label="Knowledge Base"
              icon="info"
              collapsed={false}
              onClick={() => window.open("https://intercom.help/ruebarueapp/en/", "_blank")}
            />
            <SidebarItem to="#" label="Sign Out" icon="sign-out" collapsed={false} onClick={handleLogout} />
          </ul>
          )}

          <div className="sidebar-account-info" style={{ padding: '16px 12px', borderTop: '1px solid #e5e7eb', marginTop: 'auto' }}>
            <div className="sidebar-account-info-row">
              <span className="sidebar-account-info-label">Signed in as</span>
              <span className="sidebar-account-info-value">{account?.user?.email}</span>
            </div>
            {inbox && inbox.phone && (
              <div className="sidebar-account-info-row">
                <span className="sidebar-account-info-label">SMS Number</span>
                <span className="sidebar-account-info-value">{inbox.phone}</span>
              </div>
            )}
          </div>

        </div>
      </div>

      <TrialAccount />
      <Notification />
    </>
  );
};

export default Sidebar;